import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AVAILABLE_ROLES, UserSearchRequest, apiInstance } from '../services';
import { parseUsableUserId, toUserId, toUserUuid } from '../services/service-trips-api/types';
import type { AssignPassengersRequest } from '../services/service-trips-api/types/user-v2';
import type { LocationDto } from '../services/service-trips-api/types/user-v2';
import { QueryKeys } from './types';

const getUserListPayload = (
  searchValue: string,
  pageNumber: number,
  itemsPerPage: number
): UserSearchRequest => {
  const hasSearch = searchValue.length > 0;
  const isNumber = hasSearch && !isNaN(Number(searchValue));

  return {
    nameSearch: hasSearch && !isNumber ? { name: [searchValue] } : undefined,
    userIds: isNumber ? [Number(searchValue)] : undefined,
    pageNumber,
    itemsPerPage,
  };
};

export const useUserListQuery = (
  searchString: string,
  pageNumber: number,
  itemsPerPage: number
) =>
  useQuery({
    queryKey: [QueryKeys.ListUsers, searchString, pageNumber, itemsPerPage],
    queryFn: async () => {
      const payload = getUserListPayload(
        searchString,
        pageNumber,
        itemsPerPage
      );
      const response = await apiInstance.getUserList(payload);
      return response;
    },
  });

export const useUserDetailsQuery = (userId: number) =>
  useQuery({
    queryKey: [QueryKeys.UserDetails, userId],
    queryFn: async () => {
      const userData = await apiInstance.getUser(toUserId(userId));
      return userData;
    },
  });

/** V2 variant — accepts UUID string (ENB-1610). */
export const useUserDetailsQueryV2 = (userId: string | undefined) => {
  const parsedUserId = parseUsableUserId(userId);

  return useQuery({
    queryKey: [QueryKeys.UserDetails, 'v2', parsedUserId],
    queryFn: () => {
      if (!parsedUserId) throw new Error('userId is required when the query is enabled');
      return apiInstance.getUserV2(toUserUuid(parsedUserId));
    },
    enabled: Boolean(parsedUserId),
  });
};

/** V2 search — GET with query params, Spring Page response (ENB-1610). */
export const useUserListQueryV2 = (
  params: {
    query?: string;
    roles?: string[];
    agreementId?: string;
    active?: boolean;
    page?: number;
    size?: number;
  },
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [QueryKeys.ListUsers, 'v2', params],
    queryFn: () => apiInstance.getUserListV2(params),
    enabled: options?.enabled ?? true,
  });

/** V2 assign passengers mutation — POST /v2/users/passengers/assign (ENB-1610). */
export const useAssignPassengersMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AssignPassengersRequest) =>
      apiInstance.assignPassengers(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.UserDetails] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.UserDetails, 'v2'] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.PassengerAssignedUsers] });
    },
  });
};

/** V2 delete user mutation — DELETE /v2/users/{id}. */
export const useDeleteUserMutationV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => apiInstance.deleteUserV2(toUserUuid(userId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ListUsers] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.UserDetails] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.UserDetails, 'v2'] });
      // A deleted user can still be cached in a passenger's assigned-users list (ENB-2452).
      queryClient.invalidateQueries({ queryKey: [QueryKeys.PassengerAssignedUsers] });
    },
  });
};

/** V2 resend activation email — POST /v2/users/{id}/resend-activation (no body, 204). */
export const useResendActivationMutationV2 = () =>
  useMutation({
    mutationFn: (userId: string) =>
      apiInstance.resendActivationV2(toUserUuid(userId)),
  });

export const useUserRolesQuery = () =>
  useQuery({
    queryKey: [QueryKeys.UserRoles],
    queryFn: () => apiInstance.getRoles(),
    placeholderData: () => Array.from(AVAILABLE_ROLES),
  });

/** V2 auth types — returns flat string[] (ENB-1610). */
export const useAuthenticationTypesQueryV2 = () =>
  useQuery({
    queryKey: [QueryKeys.AuthenticationTypes, 'v2'],
    queryFn: () => apiInstance.getAuthenticationTypesV2(),
  });

export const useAuthenticationTypesQuery = () =>
  useQuery({
    queryKey: [QueryKeys.AuthenticationTypes],
    queryFn: apiInstance.getAuthenticationTypes,
    throwOnError: true,
  });

interface UseAgreementsQueryOptions {
  enabled?: boolean;
  throwOnError?: boolean | ((error: unknown) => boolean);
  targetUser?: string;
}

export const useAgreementsQuery = (options?: UseAgreementsQueryOptions) =>
  useQuery({
    queryKey: options?.targetUser
      ? [QueryKeys.Agreements, options.targetUser]
      : [QueryKeys.Agreements],
    queryFn: () => apiInstance.getAgreements(options?.targetUser),
    enabled: options?.enabled ?? true,
    throwOnError: options?.throwOnError ?? true,
  });

interface UseAgreementsByIdsQueryOptions {
  throwOnError?: boolean | ((error: unknown) => boolean);
}

export const useAgreementsByIdsQuery = (
  agreementIds: number[],
  options?: UseAgreementsByIdsQueryOptions,
) =>
  useQuery({
    queryKey: [QueryKeys.Agreements, agreementIds],
    queryFn: () => apiInstance.getAgreementsByIds(agreementIds),
    enabled: agreementIds.length > 0,
    throwOnError: options?.throwOnError ?? false,
  });

/** Global list of locations (travel points) — GET /v2/locations.
 * Endpoint in progress; errors are suppressed until BE ships. */
export const useLocationsQuery = (enabled = true) =>
  useQuery<LocationDto[]>({
    queryKey: [QueryKeys.Locations],
    queryFn: () => apiInstance.getLocations(),
    enabled,
    throwOnError: false,
    staleTime: 5 * 60 * 1000,
  });
